import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CopyOutlined,
  CrownOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  ReloadOutlined,
  StarOutlined,
  UndoOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Col,
  Input,
  InputNumber,
  Layout,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { message } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { getLocalStorageItem } from "@/Utils/Index";

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

function User() {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    plan: "",
    hasCredits: "",
    hasPurchased: "",
    hasResumes: "",
    createdDate: "",
    sortOrder: "latest",
    country: "",
    accountStatus: "",
  });
  const [actionUserId, setActionUserId] = useState(null);

  const searchTimeoutRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Review modal state
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  // Resume modal state
  const [resumeModalVisible, setResumeModalVisible] = useState(false);
  const [resumeModalLoading, setResumeModalLoading] = useState(false);
  const [selectedUserResumes, setSelectedUserResumes] = useState([]);
  const [resumeModalUser, setResumeModalUser] = useState("");

  const isInitialMount = useRef(true);

  useEffect(() => {
    const accessToken = getLocalStorageItem("authorizationAdminPanel");
    if (!accessToken) {
      navigate("/app/admin/login");
    } else {
      fetchUserData();
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    fetchUserData(1, pageSize, searchTerm, filters);
  }, [pageSize]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchUserData = async (
    page = currentPage,
    size = pageSize,
    search = searchTerm,
    filterParams = filters
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: size.toString(),
      });

      if (search && search.trim()) {
        queryParams.append("search", search.trim());
      }

      if (filterParams.plan) queryParams.append("plan", filterParams.plan);
      if (filterParams.hasCredits) queryParams.append("hasCredits", filterParams.hasCredits);
      if (filterParams.hasPurchased) queryParams.append("hasPurchased", filterParams.hasPurchased);
      if (filterParams.hasResumes) queryParams.append("hasResumes", filterParams.hasResumes);
      if (filterParams.createdDate) queryParams.append("createdDate", filterParams.createdDate);
      if (filterParams.sortOrder) queryParams.append("sortOrder", filterParams.sortOrder);
      if (filterParams.country) queryParams.append("country", filterParams.country);
      if (filterParams.accountStatus) queryParams.append("accountStatus", filterParams.accountStatus);

      const response = await fetch.get(`admin/userdata?${queryParams.toString()}`);

      if (response.success && response.data) {
        const { users, pagination } = response.data;
        const processedData = processUserData(users || []);
        setData(processedData);
        setTotalItems(pagination.total || 0);
        setTotalPages(pagination.totalPages || 0);
        setCurrentPage(pagination.current || 1);
      } else {
        setData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const processUserData = (users) => {
    return users.map((user) => ({
      id: user._id,
      userId: user._id,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      fullName: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A",
      email: user.email || null,
      mobile: user.mobile || null,
      country: user.country || null,
      city: user.city || null,
      countryCode: user.countryCode || null,
      plan: user.plan || "free",
      planName: user.plan_name || null,
      planDisplayName: user.plan_display_name || null,
      recurringPlanType: user.recurringPlanType || null,
      recurringPlanName: user.recurringPlanName || null,
      recurringPlanId: user.recurringPlanId || null,
      noBranding: user.noBranding || false,
      creditBalance: user.credits?.balance ?? user.totalCredits ?? 15,
      creditTotalUsed: user.credits?.totalUsed ?? user.usedCredits ?? 0,
      creditTotalPurchased: user.credits?.totalPurchased ?? 0,
      creditFreeGiven: user.credits?.freeCreditsGiven ?? 15,
      creditLastUpdated: user.credits?.lastUpdated ? new Date(user.credits.lastUpdated) : null,
      resumeCount: user.activeResumeCount || 0,
      coverLetterCount: user.coverLetterCount || 0,
      verified: user.verified || false,
      isActive: user.isActive !== false,
      isDeleted: !!user.deletedAt,
      deletedAt: user.deletedAt ? new Date(user.deletedAt) : null,
      deletedBy: user.deletedBy || null,
      purgeAt: user.purgeAt ? new Date(user.purgeAt) : null,
      isRestricted: user.isRestricted || false,
      role: user.role || "user",
      lastLoginAt: user.lastLoginAt ? new Date(user.lastLoginAt) : null,
      createdAt: user.createdAt ? new Date(user.createdAt) : null,
      updatedAt: user.updatedAt ? new Date(user.updatedAt) : null,
      instaLink: user.instaLink || null,
      fbLink: user.fbLink || null,
      companyName: user.companyName || null,
      resumeLocation: user.resumeLocation || null,
    }));
  };

  const formatTimeAgo = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return "N/A";
  };

  const copyToClipboard = (text, type = "text") => {
    navigator.clipboard.writeText(text);
    const messages = {
      email: "Email copied!",
      phone: "Phone copied!",
      userId: "User ID copied!",
      text: "Copied!",
    };
    message.success(messages[type] || messages.text);
  };

  const handleAccessUser = useCallback(async (record) => {
    try {
      const apiResult = await fetch.get(`admin/access_token?id=${record.userId}`);
      if (apiResult.success && apiResult.data?.token) {
        window.open(`${process.env.APP_URL}/app/dashboard?adminToken=${apiResult.data.token}`, "_blank");
      } else {
        message.error(apiResult.message || "Failed to generate access token");
      }
    } catch (error) {
      console.error("Error accessing user:", error);
      message.error("Failed to access user dashboard.");
    }
  }, []);

  // Soft delete a user account. The user and all of their data stay in the
  // database (owner keeps everything, shown with an "Inactive" tag) but become
  // completely inaccessible to the user.
  const handleDeleteUser = useCallback(
    async (record) => {
      setActionUserId(record.userId);
      try {
        const response = await fetch.delete(`admin/user/${record.userId}`);
        if (response?.success !== false) {
          message.success(`${record.email || record.fullName} deleted. Data kept and marked Inactive.`);
          fetchUserData(currentPage, pageSize, searchTerm, filters);
        } else {
          message.error(response?.message || "Failed to delete user");
        }
      } catch (error) {
        message.error(error?.response?.data?.message || "Failed to delete user");
      } finally {
        setActionUserId(null);
      }
    },
    [currentPage, pageSize, searchTerm, filters]
  );

  const handleRestoreUser = useCallback(
    async (record) => {
      setActionUserId(record.userId);
      try {
        const response = await fetch.put(`admin/user/${record.userId}/restore`, {});
        if (response?.success !== false) {
          message.success(`${record.email || record.fullName} restored.`);
          fetchUserData(currentPage, pageSize, searchTerm, filters);
        } else {
          message.error(response?.message || "Failed to restore user");
        }
      } catch (error) {
        message.error(error?.response?.data?.message || "Failed to restore user");
      } finally {
        setActionUserId(null);
      }
    },
    [currentPage, pageSize, searchTerm, filters]
  );

  const handleExportCSV = () => {
    if (data.length === 0) {
      message.warning("No data to export.");
      return;
    }
    const headers = ["Name", "Email", "Phone", "Country", "City", "Plan", "Credits", "Resumes", "Created"];
    const rows = data.map((r) => [
      r.fullName,
      r.email || "",
      r.mobile || "",
      r.country || "",
      r.city || "",
      r.plan,
      r.creditBalance,
      r.resumeCount,
      r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((v) => `"${v}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success("CSV exported successfully!");
  };

  const handleViewResumes = async (record) => {
    setResumeModalUser(record.fullName);
    setResumeModalVisible(true);
    setResumeModalLoading(true);
    try {
      const response = await fetch.get(
        `admin/resumes?userId=${record.userId}&pageSize=100&page=1`
      );
      if (response.success && response.data?.resumes) {
        setSelectedUserResumes(response.data.resumes);
      } else {
        setSelectedUserResumes([]);
      }
    } catch (error) {
      console.error("Error fetching resumes:", error);
      message.error("Failed to fetch resumes.");
      setSelectedUserResumes([]);
    } finally {
      setResumeModalLoading(false);
    }
  };

  const getSourceTag = (source) => {
    const sourceMap = {
      upload: { color: "blue", label: "Upload" },
      blank: { color: "green", label: "Blank" },
      naukri: { color: "orange", label: "Naukri" },
      "five-sec-score": { color: "purple", label: "5-Sec Score" },
    };
    const info = sourceMap[source] || { color: "default", label: source || "Unknown" };
    return <Tag color={info.color}>{info.label}</Tag>;
  };

  // Credit package tiers based on totalPurchased credits
  const CREDIT_TIERS = [
    { key: "free", name: "Free", minCredits: 0, maxCredits: 0, color: "default" },
    { key: "starter", name: "Starter", minCredits: 1, maxCredits: 349, color: "cyan" },
    { key: "basic", name: "Basic", minCredits: 350, maxCredits: 799, color: "blue" },
    { key: "pro", name: "Pro", minCredits: 800, maxCredits: 1449, color: "geekblue" },
    { key: "growth", name: "Growth", minCredits: 1450, maxCredits: 2999, color: "green" },
    { key: "business", name: "Business", minCredits: 3000, maxCredits: 5999, color: "gold" },
    { key: "business_plus", name: "Business Plus", minCredits: 6000, maxCredits: 8999, color: "orange" },
    { key: "scale", name: "Scale", minCredits: 9000, maxCredits: 11999, color: "volcano" },
    { key: "enterprise", name: "Enterprise", minCredits: 12000, maxCredits: Infinity, color: "purple" },
  ];

  const getUserTier = (totalPurchased) => {
    if (!totalPurchased || totalPurchased <= 0) return CREDIT_TIERS[0]; // Free
    return CREDIT_TIERS.find((t) => totalPurchased >= t.minCredits && totalPurchased <= t.maxCredits) || CREDIT_TIERS[0];
  };

  const getPlanTagColor = (tierKey) => {
    const tier = CREDIT_TIERS.find((t) => t.key === tierKey);
    return tier?.color || "default";
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "no",
      width: 50,
      align: "center",
      render: (_, _record, index) => (
        <Text type="secondary" style={{ fontSize: "13px" }}>
          {(currentPage - 1) * pageSize + index + 1}
        </Text>
      ),
    },
    {
      title: "User Info",
      key: "userInfo",
      width: 280,
      render: (_, record) => (
        <div style={{ padding: "4px 0" }}>
          <Space size="small" style={{ marginBottom: 6 }}>
            <Avatar size="small" icon={<UserOutlined />} />
            <Text strong>{record.fullName}</Text>
          </Space>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Email: </Text>
            <Text style={{ color: "#000" }}>{record.email || "null"}</Text>
            {record.email && (
              <Button type="text" size="small" icon={<CopyOutlined />}
                onClick={() => copyToClipboard(record.email, "email")}
                style={{ marginLeft: "4px", padding: "0 4px" }}
              />
            )}
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Phone: </Text>
            <Text style={{ color: "#000" }}>{record.mobile || "null"}</Text>
            {record.mobile && (
              <Button type="text" size="small" icon={<CopyOutlined />}
                onClick={() => copyToClipboard(record.mobile, "phone")}
                style={{ marginLeft: "4px", padding: "0 4px" }}
              />
            )}
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>ID: </Text>
            <Text style={{ color: "#1890ff", fontSize: "11px", fontFamily: "monospace" }}>{record.userId}</Text>
            <Button type="text" size="small" icon={<CopyOutlined />}
              onClick={() => copyToClipboard(record.userId, "userId")}
              style={{ marginLeft: "4px", padding: "0 4px" }}
            />
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Location: </Text>
            <Text style={{ color: "#000" }}>
              {[record.city, record.country].filter(Boolean).join(", ") || record.resumeLocation || "null"}
            </Text>
          </div>

          {record.companyName && (
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Company: </Text>
              <Text style={{ color: "#000" }}>{record.companyName}</Text>
            </div>
          )}

          {record.verified && (
            <div style={{ marginTop: 4 }}>
              <Tag
                color="green"
                style={{ margin: 0 }}
              >
                Verified
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Plan & Credits",
      key: "planDetails",
      width: 220,
      render: (_, record) => {
        const tier = getUserTier(record.creditTotalPurchased);
        return (
        <div style={{ padding: "4px 0" }}>
          {/* Plan Tier */}
          <div style={{
            padding: "8px 10px",
            backgroundColor: "#f6ffed",
            borderRadius: 6,
            border: "1px solid #b7eb8f",
            marginBottom: 8,
          }}>
            <Text style={{ color: "#000", fontWeight: "500" }}>Plan: </Text>
            <Tag
              color={tier.color}
              icon={tier.key !== "free" ? <CrownOutlined /> : null}
              style={{ margin: 0 }}
            >
              {tier.name}
            </Tag>
          </div>

          {/* Credits */}
          <div style={{
            padding: "8px 10px",
            backgroundColor: "#f0f5ff",
            borderRadius: 6,
            border: "1px solid #adc6ff",
          }}>
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Balance: </Text>
              <Text strong style={{ color: record.creditBalance > 0 ? "#1890ff" : "#fa8c16" }}>
                {record.creditBalance}
              </Text>
            </div>
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Used: </Text>
              <Text style={{ color: "#000" }}>{record.creditTotalUsed}</Text>
            </div>
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Purchased: </Text>
              <Text style={{ color: record.creditTotalPurchased > 0 ? "#52c41a" : "#000" }}>
                {record.creditTotalPurchased}
              </Text>
            </div>
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Free: </Text>
              <Text style={{ color: "#000" }}>{record.creditFreeGiven}</Text>
            </div>
          </div>

          {/* Resume & Cover Letter Count */}
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <Tag
              style={{ margin: 0, cursor: record.resumeCount > 0 ? "pointer" : "default" }}
              color={record.resumeCount > 0 ? "blue" : undefined}
              onClick={() => record.resumeCount > 0 && handleViewResumes(record)}
            >
              <FileTextOutlined style={{ marginRight: 4 }} />
              Resumes: <strong>{record.resumeCount}</strong>
            </Tag>
            <Tag style={{ margin: 0 }}>
              Cover: <strong>{record.coverLetterCount}</strong>
            </Tag>
          </div>
        </div>
      );
      },
    },
    {
      title: "Actions & Status",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <div style={{ padding: "4px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          <Button
            size="small"
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleAccessUser(record)}
            block
          >
            Access
          </Button>

          <Button
            size="small"
            icon={<StarOutlined />}
            onClick={async () => {
              try {
                const response = await fetch.get(
                  `admin/resumes?search=${encodeURIComponent(record.email)}&pageSize=100&page=1`
                );
                if (response.success && response.data?.resumes) {
                  const resumesWithReview = response.data.resumes.filter((r) => r.hasReview && r.review);
                  if (resumesWithReview.length > 0) {
                    const review = resumesWithReview[0].review;
                    setSelectedReview({
                      text: review.review || review,
                      submittedAt: review.submittedAt,
                      userName: record.fullName,
                    });
                    setReviewModalVisible(true);
                  } else {
                    message.info("No reviews found for this user.");
                  }
                }
              } catch (error) {
                message.error("Failed to fetch reviews.");
              }
            }}
            block
          >
            Review
          </Button>

          {record.isDeleted ? (
            <Popconfirm
              title="Restore this account?"
              description="The user will be able to log in again and their resumes become active."
              okText="Restore"
              cancelText="Cancel"
              onConfirm={() => handleRestoreUser(record)}
            >
              <Button size="small" icon={<UndoOutlined />} loading={actionUserId === record.userId} block>
                Restore
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Delete this user?"
              description={
                <div style={{ maxWidth: 260 }}>
                  The user loses access immediately and it looks deleted to them. All data is kept
                  here and marked <b>Inactive</b>.
                </div>
              }
              icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => handleDeleteUser(record)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={actionUserId === record.userId} block>
                Delete
              </Button>
            </Popconfirm>
          )}

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Status: </Text>
            <Tooltip
              title={
                record.isDeleted
                  ? `Deleted ${record.deletedBy === "user" ? "by user" : "by owner"} ${formatTimeAgo(
                      record.deletedAt
                    )}${record.purgeAt ? ` · Purge due ${record.purgeAt.toLocaleDateString()}` : ""}`
                  : ""
              }
            >
              <Tag color={record.isActive ? "green" : "red"} style={{ margin: 0 }}>
                {record.isActive ? "Active" : "Inactive"}
              </Tag>
            </Tooltip>
          </div>
          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Created: </Text>
            <Text style={{ color: "#000" }}>{formatTimeAgo(record.createdAt)}</Text>
          </div>
          {record.lastLoginAt && (
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Login: </Text>
              <Text style={{ color: "#000" }}>{formatTimeAgo(record.lastLoginAt)}</Text>
            </div>
          )}
          {record.isRestricted && (
            <Tag color="red" style={{ margin: 0 }}>
              Restricted
            </Tag>
          )}
        </div>
      ),
    },
  ];

  // Debounced search
  const debouncedSearch = useCallback(
    (value) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchUserData(1, pageSize, value, filters);
      }, 500);
    },
    [pageSize, filters]
  );

  const handleSearch = (value) => {
    debouncedSearch(value);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleSearchClear = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchUserData(1, pageSize, "", filters);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchUserData(1, pageSize, searchTerm, newFilters);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUserData(page, pageSize, searchTerm, filters);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchUserData(newPage, pageSize, searchTerm, filters);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchUserData(newPage, pageSize, searchTerm, filters);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 6) {
        for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      } else if (currentPage >= totalPages - 5) {
        for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) pages.push(i);
      } else {
        for (let i = currentPage - 5; i <= currentPage + 4; i++) pages.push(i);
      }
    }
    return pages;
  };

  const defaultFilters = {
    plan: "",
    hasCredits: "",
    hasPurchased: "",
    hasResumes: "",
    createdDate: "",
    sortOrder: "latest",
    country: "",
    accountStatus: "",
  };

  const hasActiveFilters =
    filters.plan ||
    filters.hasCredits ||
    filters.hasPurchased ||
    filters.hasResumes ||
    filters.createdDate ||
    filters.country ||
    filters.accountStatus;

  return (
    <Layout.Content style={{ padding: "24px", width: "100%", overflow: "hidden" }}>
      <style>{`
        .userdata-search .ant-input::placeholder,
        .userdata-search .ant-input-affix-wrapper input::placeholder {
          color: #666666 !important;
          opacity: 1 !important;
        }
        .userdata-filter .ant-select-selector .ant-select-selection-placeholder {
          color: #666666 !important;
          opacity: 1 !important;
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden", marginTop: "12px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Header with Search Bar and Refresh Button */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "800px" }}>
              <Search
                className="userdata-search"
                placeholder="Search by name, email, phone, city, country, user ID..."
                allowClear
                enterButton="Search"
                size="large"
                value={searchInput}
                onSearch={handleSearch}
                onChange={handleSearchInputChange}
                onClear={handleSearchClear}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Tooltip title="Export CSV">
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={handleExportCSV}
                  disabled={data.length === 0}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  Export CSV
                </Button>
              </Tooltip>

              <Tooltip title="Refresh Data">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  size="large"
                  loading={loading}
                  onClick={() => fetchUserData(currentPage, pageSize, searchTerm, filters)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  Refresh
                </Button>
              </Tooltip>

              <div
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                  minWidth: "200px",
                  textAlign: "center",
                }}
              >
                <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                  {hasActiveFilters ? "Filtered: " : "Total: "}
                  {totalItems.toLocaleString()}
                </Text>
                <Text style={{ fontSize: "12px", color: "#666", marginLeft: "4px" }}>
                  user{totalItems === 1 ? "" : "s"}
                </Text>
              </div>
            </div>
          </div>

          {/* Filter Row */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
            <div style={{ flex: 1 }}>
              <Select
                className="userdata-filter"
                placeholder="Plan"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.plan || undefined}
                onChange={(value) => handleFilterChange("plan", value || "")}
              >
                {CREDIT_TIERS.map((tier) => (
                  <Option key={tier.key} value={tier.key}>
                    {tier.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="userdata-filter"
                placeholder="Has Credits"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.hasCredits || undefined}
                onChange={(value) => handleFilterChange("hasCredits", value || "")}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="userdata-filter"
                placeholder="Has Purchased"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.hasPurchased || undefined}
                onChange={(value) => handleFilterChange("hasPurchased", value || "")}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <InputNumber
                className="userdata-filter"
                placeholder="Resume Count"
                min={0}
                size="small"
                style={{ width: "100%" }}
                value={filters.hasResumes !== "" ? Number(filters.hasResumes) : null}
                onChange={(value) => handleFilterChange("hasResumes", value !== null ? String(value) : "")}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="userdata-filter"
                placeholder="Created Date"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.createdDate || undefined}
                onChange={(value) => handleFilterChange("createdDate", value || "")}
              >
                <Option value="today">Today</Option>
                <Option value="yesterday">Yesterday</Option>
                <Option value="last_3_days">Last 3 Days</Option>
                <Option value="last_7_days">Last 7 Days</Option>
                <Option value="last_15_days">Last 15 Days</Option>
                <Option value="last_30_days">Last 30 Days</Option>
                <Option value="last_60_days">Last 60 Days</Option>
                <Option value="last_90_days">Last 90 Days</Option>
                <Option value="last_6_months">Last 6 Months</Option>
                <Option value="last_year">Last Year</Option>
                <Option value="week">This Week</Option>
                <Option value="month">This Month</Option>
                <Option value="year">This Year</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="userdata-filter"
                placeholder="Account Status"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.accountStatus || undefined}
                onChange={(value) => handleFilterChange("accountStatus", value || "")}
              >
                <Option value="active">Active</Option>
                <Option value="deleted">Deleted</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="userdata-filter"
                placeholder="Sort Order"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.sortOrder || undefined}
                onChange={(value) => handleFilterChange("sortOrder", value || "latest")}
              >
                <Option value="latest">Latest First</Option>
                <Option value="oldest">Oldest First</Option>
              </Select>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Row style={{ marginBottom: "16px" }}>
              <Col span={24}>
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  <Text strong style={{ marginRight: "8px", color: "#666" }}>
                    Active Filters:
                  </Text>
                  {filters.plan && (
                    <Tag closable onClose={() => handleFilterChange("plan", "")} color={getPlanTagColor(filters.plan)} style={{ marginRight: "4px" }}>
                      Plan: {CREDIT_TIERS.find((t) => t.key === filters.plan)?.name || filters.plan}
                    </Tag>
                  )}
                  {filters.hasCredits && (
                    <Tag closable onClose={() => handleFilterChange("hasCredits", "")} color="blue" style={{ marginRight: "4px" }}>
                      Has Credits: {filters.hasCredits === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  {filters.hasPurchased && (
                    <Tag closable onClose={() => handleFilterChange("hasPurchased", "")} color="green" style={{ marginRight: "4px" }}>
                      Has Purchased: {filters.hasPurchased === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  {filters.hasResumes && (
                    <Tag closable onClose={() => handleFilterChange("hasResumes", "")} color="magenta" style={{ marginRight: "4px" }}>
                      Resumes: {filters.hasResumes}
                    </Tag>
                  )}
                  {filters.createdDate && (
                    <Tag closable onClose={() => handleFilterChange("createdDate", "")} color="purple" style={{ marginRight: "4px" }}>
                      Date: {filters.createdDate.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Tag>
                  )}
                  {filters.sortOrder && filters.sortOrder !== "latest" && (
                    <Tag closable onClose={() => handleFilterChange("sortOrder", "latest")} color="cyan" style={{ marginRight: "4px" }}>
                      Sort: Oldest First
                    </Tag>
                  )}
                  {filters.country && (
                    <Tag closable onClose={() => handleFilterChange("country", "")} color="orange" style={{ marginRight: "4px" }}>
                      Country: {filters.country}
                    </Tag>
                  )}
                  <Button
                    type="text"
                    size="small"
                    style={{ marginLeft: "8px", color: "#666" }}
                    onClick={() => {
                      setFilters(defaultFilters);
                      setCurrentPage(1);
                      fetchUserData(1, pageSize, searchTerm, defaultFilters);
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          {/* Data Table */}
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            pagination={false}
            size="middle"
          />

          {/* Page Size Selector */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "16px",
              gap: "8px",
            }}
          >
            <Text style={{ fontSize: "14px", color: "#666" }}>Items per page:</Text>
            <Select
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
                fetchUserData(1, value, searchTerm, filters);
              }}
              style={{ width: "80px" }}
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </div>

          {/* Custom Pagination */}
          {totalItems > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              <Button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                style={{
                  minWidth: "80px",
                  height: "32px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "#fff",
                  color: currentPage === 1 ? "#d9d9d9" : "#1890ff",
                  marginRight: "8px",
                }}
              >
                Previous
              </Button>

              {totalPages > 1 &&
                getPageNumbers().map((pageNum) => (
                  <Button
                    key={pageNum}
                    type={currentPage === pageNum ? "primary" : "default"}
                    onClick={() => handlePageChange(pageNum)}
                    style={{
                      minWidth: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      border: currentPage === pageNum ? "none" : "1px solid #d9d9d9",
                      backgroundColor: currentPage === pageNum ? "#1890ff" : "transparent",
                      color: currentPage === pageNum ? "#fff" : "#262626",
                    }}
                  >
                    {pageNum}
                  </Button>
                ))}

              {totalPages === 1 && (
                <span style={{ fontSize: "14px", color: "#666", fontWeight: "500", margin: "0 16px" }}>
                  Page 1 of 1
                </span>
              )}

              <Button
                disabled={currentPage === totalPages}
                onClick={handleNext}
                style={{
                  minWidth: "80px",
                  height: "32px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === totalPages ? "#f5f5f5" : "#fff",
                  color: currentPage === totalPages ? "#d9d9d9" : "#1890ff",
                  marginLeft: "8px",
                }}
              >
                Next
              </Button>
            </div>
          )}

          {/* Show count info */}
          <div style={{ textAlign: "center", marginTop: "16px", color: "#666", fontSize: "14px" }}>
            Showing {Math.min(startIndex + 1, totalItems)}-{endIndex} of {totalItems} user
            {totalItems === 1 ? "" : "s"}
            {totalItems > 0 && (
              <span style={{ marginLeft: "16px", color: "#999" }}>
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        </Space>
      </div>

      {/* Review Modal */}
      <Modal
        open={reviewModalVisible}
        title={
          <Space>
            <StarOutlined style={{ color: "#faad14" }} />
            <span>Review by {selectedReview?.userName}</span>
          </Space>
        }
        onCancel={() => setReviewModalVisible(false)}
        footer={null}
      >
        {selectedReview ? (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                padding: 16,
                backgroundColor: "#fafafa",
                borderRadius: 8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                border: "1px solid #f0f0f0",
                color: "#595959",
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              {selectedReview.text}
            </div>
            {selectedReview.submittedAt && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Submitted: {new Date(selectedReview.submittedAt).toLocaleString()}
              </Text>
            )}
          </div>
        ) : (
          <Text>No review found.</Text>
        )}
      </Modal>

      {/* Resume List Modal */}
      <Modal
        open={resumeModalVisible}
        title={
          <Space>
            <FileTextOutlined style={{ color: "#1890ff" }} />
            <span>Resumes by {resumeModalUser}</span>
          </Space>
        }
        onCancel={() => {
          setResumeModalVisible(false);
          setSelectedUserResumes([]);
        }}
        footer={null}
        width={700}
      >
        {resumeModalLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <LoadingOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Loading resumes...</Text>
            </div>
          </div>
        ) : selectedUserResumes.length === 0 ? (
          <Text type="secondary">No resumes found.</Text>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {selectedUserResumes.map((resume, index) => (
              <div
                key={resume._id || index}
                style={{
                  padding: "12px 14px",
                  backgroundColor: "#fafafa",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <Text strong>
                    {resume.parsedData?.personalInfo?.name || resume.fileName || `Resume ${index + 1}`}
                  </Text>
                  <Space size={4}>
                    {getSourceTag(resume.resumeSource || resume.creationMode)}
                    {resume.fileType && (
                      <Tag>{resume.fileType.toUpperCase()}</Tag>
                    )}
                  </Space>
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <Text style={{ color: "#000", fontWeight: "500" }}>Status: </Text>
                    <Tag color={resume.status === "completed" ? "green" : resume.status === "failed" ? "red" : "processing"}>
                      {resume.status || "N/A"}
                    </Tag>
                  </div>
                  {resume.fileName && (
                    <div>
                      <Text style={{ color: "#000", fontWeight: "500" }}>File: </Text>
                      <Text style={{ color: "#000" }}>{resume.fileName}</Text>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
                  {resume.hasDownloaded && <Tag color="cyan">Downloaded</Tag>}
                  {resume.hasPreview && <Tag color="geekblue">Previewed</Tag>}
                  {resume.hasCoverLetter && <Tag color="purple">Cover Letter</Tag>}
                  {resume.isPublic && <Tag color="gold">Public</Tag>}
                  {resume.hasReview && <Tag color="orange">Has Review</Tag>}
                </div>
                <div style={{ marginTop: 6, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <Text style={{ color: "#000", fontWeight: "500", fontSize: "12px" }}>ID: </Text>
                    <Text copyable={{ icon: <CopyOutlined style={{ fontSize: 11 }} /> }} code style={{ fontSize: "11px" }}>
                      {resume._id}
                    </Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Created: {resume.createdAt ? new Date(resume.createdAt).toLocaleString() : "N/A"}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </Layout.Content>
  );
}

export default User;
